import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { AppHeader } from '@/components/AppHeader';
import { User } from '@supabase/supabase-js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Loader2, 
  User as UserIcon, 
  CreditCard, 
  FileText, 
  Settings as SettingsIcon, 
  ExternalLink, 
  Calendar,
  BarChart3,
  Shield,
  HelpCircle,
  Key,
  Activity
} from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { AvatarUpload } from '@/components/account/AvatarUpload';
import { ProgressStats } from '@/components/account/ProgressStats';
import { HelpSupport } from '@/components/account/HelpSupport';
import { SettingsTab } from '@/components/account/SettingsTab';
import { DailyMotivation } from '@/components/account/DailyMotivation';
import PrivacyDashboard from '@/components/PrivacyDashboard';
import { COUNTRIES } from '@/lib/countries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import WhoopConnection from '@/components/WhoopConnection';
import TrackPreferences from '@/components/settings/TrackPreferences';
import { ILPreferences } from '@/components/settings/ILPreferences';
import AppSupport from '@/components/settings/AppSupport';
import { UserComplaintForm } from '@/components/UserComplaintForm';
import { TherapyDataManagement } from '@/components/TherapyDataManagement';
import { TherapistConnection } from '@/components/account/TherapistConnection';

interface ProfileData {
  display_name: string;
  email: string;
  location: string;
  avatar_url?: string;
}

interface SubscriptionDetails {
  subscription_status?: string;
  product_id?: string;
  created_at?: string;
  current_period_end?: string;
  price_id?: string;
  stripe_customer_id?: string;
}

export default function Account() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData>({
    display_name: '',
    email: '',
    location: '',
    avatar_url: '',
  });
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails>({});
  
  // Get tab from URL or default to 'profile'
  const activeTab = searchParams.get('tab') || 'profile';
  
  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };

  const { 
    totalCredits, 
    monthlyCredits, 
    purchasedCredits, 
    trialEndDate, 
    isTrialActive,
    openCustomerPortal 
  } = useSubscription();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate('/auth');
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate('/auth');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchAccountData();
    }
  }, [user]);

  const fetchAccountData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch profile data
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('display_name, location, avatar_url')
        .eq('user_id', user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      // Fetch subscription details
      const { data: subscription, error: subError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (subError && subError.code !== 'PGRST116') {
        // No subscription found - expected for free tier users
      }

      setProfileData({
        display_name: profile?.display_name || '',
        email: user.email || '',
        location: profile?.location || '',
        avatar_url: profile?.avatar_url || '',
      });

      setSubscriptionDetails(subscription || {});
    } catch (error) {
      console.error('Error fetching account data:', error);
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    try {
      setSaving(true);

      // Get previous location for analytics
      const { data: oldProfile } = await supabase
        .from('profiles')
        .select('location')
        .eq('user_id', user.id)
        .single();

      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: profileData.display_name,
          location: profileData.location,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Track location change for marketing analytics
      if (profileData.location !== oldProfile?.location) {
        const country = COUNTRIES.find(c => c.code === profileData.location);
        
        await supabase.from('analytics_events').insert({
          user_id: user.id,
          session_id: crypto.randomUUID(),
          event_name: 'location_updated',
          event_type: 'user_profile',
          properties: {
            country_code: profileData.location,
            country_name: country?.name,
            region: country?.region,
            previous_location: oldProfile?.location,
          }
        });
      }

      // Update email if changed
      if (profileData.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: profileData.email,
        });
        if (emailError) throw emailError;
        toast.success('Profile updated! Please check your new email to confirm the change.');
      } else {
        toast.success('Profile updated successfully');
      }

      fetchAccountData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  };

  const handleManageSubscription = async () => {
    try {
      await openCustomerPortal();
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open subscription management');
    }
  };

  const getSubscriptionStatusBadge = () => {
    if (isTrialActive) {
      return <Badge variant="outline" className="bg-blue-500/10 text-blue-600">Free Trial</Badge>;
    }
    
    switch (subscriptionDetails.subscription_status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'trialing':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600">Trialing</Badge>;
      case 'canceled':
        return <Badge variant="destructive">Cancelled</Badge>;
      case 'past_due':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600">Past Due</Badge>;
      default:
        return <Badge variant="secondary">Free Plan</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader user={user} onSignOut={handleSignOut} />
        <div className="container mx-auto py-12 px-6">
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <div className="container mx-auto py-12 px-6 max-w-6xl">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-foreground">My Account</h1>
          <p className="text-muted-foreground mt-2">Manage your profile, subscription, and account settings</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-full overflow-x-auto" style={{ gridTemplateColumns: 'repeat(8, 1fr)' }}>
            <TabsTrigger value="profile" className="gap-2">
              <UserIcon className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-2">
              <CreditCard className="h-4 w-4" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="billing" className="gap-2">
              <FileText className="h-4 w-4" />
              Billing
            </TabsTrigger>
            <TabsTrigger value="integrations" className="gap-2">
              <Activity className="h-4 w-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="progress" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              Progress
            </TabsTrigger>
            <TabsTrigger value="privacy" className="gap-2">
              <Shield className="h-4 w-4" />
              Privacy
            </TabsTrigger>
            <TabsTrigger value="help" className="gap-2">
              <HelpCircle className="h-4 w-4" />
              Help
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <DailyMotivation userId={user?.id || ''} />
            <Card>
              <CardHeader>
                <CardTitle>Profile Picture</CardTitle>
                <CardDescription>
                  Upload a photo to personalise your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AvatarUpload
                  userId={user?.id || ''}
                  currentAvatarUrl={profileData.avatar_url}
                  displayName={profileData.display_name}
                  onAvatarUpdate={(url) => setProfileData({ ...profileData, avatar_url: url })}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Update your personal details and contact information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="display_name">Display Name</Label>
                    <Input
                      id="display_name"
                      value={profileData.display_name}
                      onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location / Country</Label>
                  <Select
                    value={profileData.location}
                    onValueChange={(value) => setProfileData({ ...profileData, location: value })}
                  >
                    <SelectTrigger id="location">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] bg-background">
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Helps us understand our user base for product improvements
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSaveProfile} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => fetchAccountData()}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Account Security
                </CardTitle>
                <CardDescription>
                  Manage your password and authentication
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className="font-medium">Password</p>
                      <p className="text-sm text-muted-foreground">Manage your account password</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={async () => {
                        try {
                          const { error } = await supabase.auth.resetPasswordForEmail(
                            user?.email || '',
                            { redirectTo: `${window.location.origin}/auth?mode=reset` }
                          );
                          if (error) throw error;
                          toast.success('Password reset email sent. Check your inbox.');
                        } catch (error) {
                          console.error('Password reset error:', error);
                          toast.error('Failed to send password reset email');
                        }
                      }}
                    >
                      Change Password
                    </Button>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t">
                    <div>
                      <p className="font-medium">Account Created</p>
                      <p className="text-sm text-muted-foreground">
                        {user?.created_at && format(new Date(user.created_at), 'PPP')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t">
                    <div>
                      <p className="font-medium">Last Login</p>
                      <p className="text-sm text-muted-foreground">
                        {user?.last_sign_in_at && format(new Date(user.last_sign_in_at), 'PPP p')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Current Plan</span>
                  {getSubscriptionStatusBadge()}
                </CardTitle>
                <CardDescription>
                  Manage your subscription and view available upgrades
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Credits Section */}
                <div className="p-4 rounded-lg bg-accent/50 border">
                  <h3 className="font-semibold mb-3">Available Credits</h3>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-primary">{totalCredits}</div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{monthlyCredits}</div>
                      <div className="text-xs text-muted-foreground">Monthly</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{purchasedCredits}</div>
                      <div className="text-xs text-muted-foreground">Purchased</div>
                    </div>
                  </div>
                </div>

                {/* Subscription Details */}
                {subscriptionDetails.subscription_status && (
                  <div className="space-y-3">
                    {subscriptionDetails.price_id && (
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-sm text-muted-foreground">Price ID</span>
                        <span className="font-medium text-xs">{subscriptionDetails.price_id}</span>
                      </div>
                    )}
                    {subscriptionDetails.created_at && (
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-sm text-muted-foreground">Started</span>
                        <span className="font-medium flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(subscriptionDetails.created_at), 'PP')}
                        </span>
                      </div>
                    )}
                    {subscriptionDetails.current_period_end && (
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-sm text-muted-foreground">Next Billing Date</span>
                        <span className="font-medium flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(subscriptionDetails.current_period_end), 'PP')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Trial Info */}
                {isTrialActive && trialEndDate && (
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-sm text-blue-600 flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Your free trial ends on {format(new Date(trialEndDate), 'PP')}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 pt-4">
                  <Button onClick={() => navigate('/subscription')} variant="default">
                    View Available Plans
                  </Button>
                  {subscriptionDetails.stripe_customer_id && (
                    <Button onClick={handleManageSubscription} variant="outline">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Manage Subscription
                    </Button>
                  )}
                  <Button onClick={() => navigate('/credits')} variant="outline">
                    Purchase Credits
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Billing & Payment</CardTitle>
                <CardDescription>
                  Manage your payment methods and view billing history
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscriptionDetails.stripe_customer_id ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Your payment methods and billing history are managed through Stripe's secure customer portal.
                    </p>
                    <Button onClick={handleManageSubscription} className="w-full sm:w-auto">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Billing Portal
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">No payment information on file</p>
                    <Button onClick={() => navigate('/subscription')}>
                      Subscribe to Add Payment Method
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-6">
            <WhoopConnection />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <TrackPreferences />
            <ILPreferences />
            <SettingsTab userId={user?.id || ''} />
          </TabsContent>

          {/* Progress & Statistics Tab */}
          <TabsContent value="progress" className="space-y-6">
            <ProgressStats userId={user?.id || ''} />
          </TabsContent>

          {/* Privacy & Data Management Tab */}
          <TabsContent value="privacy" className="space-y-6">
            <TherapyDataManagement />
            {user && (
              <PrivacyDashboard 
                user={user} 
                inline={true}
              />
            )}
          </TabsContent>

          {/* Help & Support Tab */}
          <TabsContent value="help" className="space-y-6">
            <AppSupport />
            <HelpSupport userId={user?.id || ''} />
            <Card>
              <CardHeader>
                <CardTitle>Submit a Complaint</CardTitle>
                <CardDescription>
                  Have feedback or concerns? Let us know so we can improve.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UserComplaintForm />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
